import React, { useState, useMemo, useEffect, useCallback } from 'react'
import {
  Avatar,
  Button,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  TableHead,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Autocomplete,
  TextField,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { useIntl } from 'react-intl'
import { colorPalette } from '../../../../style/colorPalette'
import {
  SpiAdjustmentItem,
  SprintSelectionItem,
  formatSprintLabel,
  EXPANDED_STATUSES,
  COLLAPSED_STATUSES,
  groupByStatus,
} from './types'
import { SprintStatus } from '../../../../../lib/functions/sprint'
import { TableHeaderCell, TableDataCell } from './StyledComponents'
import { NumberTextInput } from '../../../../components/inputs/NumberTextInput'
import { UserBasic } from '../../../../../lib/functions/user'
import { useProjectMemberRepository } from '../../../../../services/adaptors/projectMemberRepositoryAdaptor'
import { useDebounce } from '../../../../hooks/useDebounce'
import { useExpandableStatuses } from './useExpandableStatuses'

interface Props {
  spiItems: SpiAdjustmentItem[]
  selectionItems: SprintSelectionItem[]
  workloadUnitLabel: string
  addMenuAnchor: HTMLElement | null
  onAddMenuOpen: (event: React.MouseEvent<HTMLButtonElement>) => void
  onAddMenuClose: () => void
  onAddSpi: (sprintUuid: string) => void
  onRemoveSpi: (spiUuid: string) => void
  onWorkloadChange: (spiUuid: string, value: string) => void
  onAssigneeChange: (spiUuid: string, assignee: UserBasic | null) => void
  onSwitchToSelection: () => void
  formatDate: (dateStr: string) => string
  convertWorkloadFromHourToSelectedUnit: (hour: number) => number
}

/**
 * ステータス別にグルーピングされたスプリント追加メニュー
 */
const GroupedSprintMenu = ({
  items,
  onAddSpi,
  formatDate,
  onClose,
}: {
  items: SprintSelectionItem[]
  onAddSpi: (sprintUuid: string) => void
  formatDate: (dateStr: string) => string
  onClose: () => void
}) => {
  const { groups, unsetItems } = useMemo(() => groupByStatus(items), [items])
  const { expandedStatuses, toggleStatus } = useExpandableStatuses()

  const renderStatusGroup = (status: SprintStatus) => {
    const group = groups.get(status)
    if (!group) return null
    return (
      <React.Fragment key={status}>
        <ListItemButton onClick={() => toggleStatus(status)} sx={{ py: 0.5 }}>
          <ListItemText
            primary={status}
            primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.85rem' }}
          />
          {expandedStatuses.has(status) ? (
            <ExpandLess fontSize="small" />
          ) : (
            <ExpandMore fontSize="small" />
          )}
        </ListItemButton>
        <Collapse
          in={expandedStatuses.has(status)}
          timeout="auto"
          unmountOnExit={true}
        >
          <List dense={true} disablePadding={true}>
            {group.map(item => (
              <MenuItem
                key={item.sprintUuid}
                onClick={() => {
                  onAddSpi(item.sprintUuid)
                  onClose()
                }}
                sx={{ pl: 4 }}
              >
                {formatSprintLabel(
                  item.sprintName,
                  item.startDate,
                  item.endDate,
                  formatDate
                )}
              </MenuItem>
            ))}
          </List>
        </Collapse>
      </React.Fragment>
    )
  }

  return (
    <List dense={true} sx={{ py: 0, minWidth: 240 }}>
      {/* INPROGRESS → STANDBY（展開状態） */}
      {EXPANDED_STATUSES.map(renderStatusGroup)}
      {/* スプリント未設定 */}
      {unsetItems.map(item => (
        <MenuItem
          key={item.sprintUuid}
          onClick={() => {
            onAddSpi(item.sprintUuid)
            onClose()
          }}
        >
          {formatSprintLabel(
            item.sprintName,
            item.startDate,
            item.endDate,
            formatDate
          )}
        </MenuItem>
      ))}
      {/* FINISHED → CANCELED（折りたたみ状態） */}
      {COLLAPSED_STATUSES.map(renderStatusGroup)}
    </List>
  )
}

export const WorkloadModeView: React.FC<Props> = ({
  spiItems,
  selectionItems,
  workloadUnitLabel,
  addMenuAnchor,
  onAddMenuOpen,
  onAddMenuClose,
  onAddSpi,
  onRemoveSpi,
  onWorkloadChange,
  onAssigneeChange,
  onSwitchToSelection,
  formatDate,
  convertWorkloadFromHourToSelectedUnit,
}) => {
  const intl = useIntl()

  // 工数入力のローカル表示値（空文字入力を許容するため）
  const [workloadInputValues, setWorkloadInputValues] = useState<
    Record<string, string>
  >({})

  return (
    <>
      {/* アクションバー */}
      <Box
        sx={{
          px: 3,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          startIcon={<AddIcon />}
          onClick={onAddMenuOpen}
          disabled={selectionItems.length === 0}
          size="small"
        >
          {intl.formatMessage({
            id: 'productBacklog.sprintInteractiveAdjustment.addSprint',
          })}
        </Button>
        <Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={onAddMenuClose}
          slotProps={{
            paper: { sx: { maxHeight: 400, overflow: 'auto' } },
          }}
        >
          <GroupedSprintMenu
            items={selectionItems}
            onAddSpi={onAddSpi}
            formatDate={formatDate}
            onClose={onAddMenuClose}
          />
        </Menu>
        <Button size="small" onClick={onSwitchToSelection}>
          {intl.formatMessage({
            id: 'productBacklog.sprintInteractiveAdjustment.editSelection',
          })}
        </Button>
      </Box>
      {/* SPI一覧テーブル */}
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader={true}>
          <TableHead>
            <TableRow>
              <TableHeaderCell>
                {intl.formatMessage({
                  id: 'productBacklog.sprintInteractiveAdjustment.sprint',
                })}
              </TableHeaderCell>
              <TableHeaderCell sx={{ width: 160 }}>
                {intl.formatMessage({
                  id: 'productBacklog.sprintInteractiveAdjustment.pbiAssignee',
                })}
              </TableHeaderCell>
              <TableHeaderCell align="right">
                {intl.formatMessage({
                  id: 'productBacklog.sprintInteractiveAdjustment.estimatedHour',
                })}
                ({workloadUnitLabel})
              </TableHeaderCell>
              <TableHeaderCell align="center" sx={{ width: 50 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {spiItems.length === 0 ? (
              <TableRow>
                <TableDataCell colSpan={4} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {intl.formatMessage({
                      id: 'productBacklog.sprintInteractiveAdjustment.noSprints',
                    })}
                  </Typography>
                </TableDataCell>
              </TableRow>
            ) : (
              spiItems.map(item => (
                <TableRow key={item.uuid} hover={true}>
                  <TableDataCell>
                    {formatSprintLabel(
                      item.sprintName,
                      item.startDate,
                      item.endDate,
                      formatDate
                    )}
                  </TableDataCell>
                  <TableDataCell
                    sx={{
                      verticalAlign: 'middle',
                      padding: 0,
                    }}
                  >
                    <AssigneeAutocomplete
                      value={item.assignee}
                      onChange={assignee =>
                        onAssigneeChange(item.uuid, assignee)
                      }
                      placeholder={intl.formatMessage({
                        id: 'productBacklog.sprintInteractiveAdjustment.searchAssignee',
                      })}
                    />
                  </TableDataCell>
                  <TableDataCell align="right">
                    <NumberTextInput
                      value={
                        workloadInputValues[item.uuid] ??
                        convertWorkloadFromHourToSelectedUnit(
                          item.estimatedHour
                        )
                      }
                      onChange={e => {
                        const value = e.target.value
                        setWorkloadInputValues(prev => ({
                          ...prev,
                          [item.uuid]: value,
                        }))
                        onWorkloadChange(item.uuid, value)
                      }}
                      inputProps={{
                        min: 0,
                        step: 1,
                        onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                          e.target.select(),
                        onBlur: () => {
                          // フォーカスが外れたらローカル値をクリア（親データに同期）
                          setWorkloadInputValues(prev => {
                            const newValues = { ...prev }
                            delete newValues[item.uuid]
                            return newValues
                          })
                        },
                      }}
                      sx={{
                        width: 100,
                        '& > input': {
                          textAlign: 'right',
                          padding: '6px 8px',
                          border: `1px solid ${colorPalette.monotone[2]}`,
                          borderRadius: '4px',
                          '&:focus': {
                            borderColor: colorPalette.skyBlue[7],
                            outline: 'none',
                          },
                        },
                      }}
                    />
                  </TableDataCell>
                  <TableDataCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => onRemoveSpi(item.uuid)}
                      sx={{ color: colorPalette.monotone[4] }}
                      tabIndex={-1}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableDataCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

// セル内Autocompleteコンポーネント（サーバーサイド検索でローマ字対応）
interface AssigneeAutocompleteProps {
  value: UserBasic | null | undefined
  onChange: (assignee: UserBasic | null) => void
  placeholder: string
}

const AssigneeAutocomplete = React.memo<AssigneeAutocompleteProps>(
  ({ value, onChange, placeholder }) => {
    const [searchText, setSearchText] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const [options, setOptions] = useState<UserBasic[]>([])
    const { searchAsProjectMemberSearchOption } = useProjectMemberRepository()
    const debouncedSearchText = useDebounce(searchText, 300)

    // サーバーサイド検索（ローマ字対応）
    useEffect(() => {
      if (!isOpen) return
      let isMounted = true
      const fn = async () => {
        const response =
          await searchAsProjectMemberSearchOption(debouncedSearchText)
        if (isMounted) {
          setOptions(
            response.map(m => ({
              uuid: m.uuid,
              name: m.name,
              code: m.code,
              iconUrl: m.iconUrl ?? '',
            }))
          )
        }
      }
      fn()
      return () => {
        isMounted = false
      }
    }, [searchAsProjectMemberSearchOption, debouncedSearchText, isOpen])

    const handleOpen = useCallback(() => {
      setIsOpen(true)
      setSearchText('')
    }, [])

    const handleClose = useCallback(() => {
      setIsOpen(false)
      setSearchText('')
    }, [])

    // ドロップダウン表示中は検索テキスト、閉じているときは担当者名を表示
    const displayedInputValue = isOpen ? searchText : (value?.name ?? '')

    return (
      <Autocomplete
        value={value ?? null}
        inputValue={displayedInputValue}
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === 'input') {
            setSearchText(newInputValue)
          }
        }}
        options={options}
        filterOptions={x => x}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(option, val) => option.uuid === val.uuid}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.uuid}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Avatar src={option.iconUrl} sx={{ width: 24, height: 24 }} />
            <Typography variant="body2">{option.name}</Typography>
          </Box>
        )}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            placeholder={placeholder}
            inputProps={{
              ...params.inputProps,
              onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                e.target.select(),
            }}
          />
        )}
        onChange={(_, newValue) => onChange(newValue)}
        size="small"
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root': {
            padding: '2px 8px',
            '& fieldset': {
              borderColor: colorPalette.monotone[2],
            },
            '&:hover fieldset': {
              borderColor: colorPalette.skyBlue[7],
            },
            '&.Mui-focused fieldset': {
              borderColor: colorPalette.skyBlue[7],
              borderWidth: 1,
            },
          },
        }}
        openOnFocus={true}
        autoHighlight={true}
      />
    )
  },
  // onChangeは毎回新しい関数なので比較から除外
  (prevProps, nextProps) =>
    prevProps.value?.uuid === nextProps.value?.uuid &&
    prevProps.placeholder === nextProps.placeholder
)
